/**
 * Soroban Render JSON Format Parser
 *
 * JSON UI Format v1 Specification:
 *
 * {
 *   "format": "soroban-render-json-v1",
 *   "title": "Optional page title",
 *   "components": [Component, ...]
 * }
 *
 * Component Types:
 * - heading: { type: "heading", level: 1-6, text: "string" }
 * - text: { type: "text", content: "string" }
 * - markdown: { type: "markdown", content: "markdown string" }
 * - divider: { type: "divider" }
 * - form: { type: "form", action: "method_name", fields: [Field, ...], submitLabel: "string" }
 * - button: { type: "button", action: "tx" | "render", method?: "string", path?: "string", args?: {}, label: "string" }
 * - list: { type: "list", ordered?: boolean, items: [ListItem | Component, ...] }
 * - task: { type: "task", id: number, text: "string", completed: boolean, actions?: [Button, ...] }
 * - navigation: { type: "navigation", items: [{ label: "string", path: "string" }, ...] }
 * - container: { type: "container", className?: "string", components: [Component, ...] }
 * - include: { type: "include", contract: "string", path?: "string" } (Phase 4)
 *
 * Field Types:
 * - { name: "string", type: "text" | "number" | "email" | "password" | "textarea", placeholder?: "string", required?: boolean, value?: "string" }
 * - { name: "string", type: "select", options: [{ value: "string", label: "string" }, ...], required?: boolean }
 * - { name: "string", type: "checkbox", label?: "string", checked?: boolean }
 */
/**
 * Parse a JSON string into a JsonUIDocument
 */
export function parseJsonUI(content) {
    try {
        const parsed = JSON.parse(content);
        if (!parsed.format || !parsed.format.startsWith("soroban-render-json")) {
            return {
                success: false,
                error: "Invalid format: missing or invalid 'format' field",
            };
        }
        if (parsed.format !== "soroban-render-json-v1") {
            return {
                success: false,
                error: `Unsupported format version: ${parsed.format}`,
            };
        }
        if (!Array.isArray(parsed.components)) {
            return {
                success: false,
                error: "Invalid document: 'components' must be an array",
            };
        }
        // Validate components
        const validationError = validateComponents(parsed.components);
        if (validationError) {
            return {
                success: false,
                error: validationError,
            };
        }
        return {
            success: true,
            document: parsed,
        };
    }
    catch (err) {
        return {
            success: false,
            error: err instanceof Error ? err.message : "Failed to parse JSON",
        };
    }
}
const VALID_COMPONENT_TYPES = [
    "heading",
    "text",
    "markdown",
    "divider",
    "form",
    "button",
    "list",
    "task",
    "navigation",
    "container",
    "include",
    "chart",
];
function validateComponents(components) {
    for (let i = 0; i < components.length; i++) {
        const component = components[i];
        if (!component || typeof component !== "object") {
            return `Component at index ${i} is not an object`;
        }
        if (!component.type || typeof component.type !== "string") {
            return `Component at index ${i} is missing 'type' field`;
        }
        if (!VALID_COMPONENT_TYPES.includes(component.type)) {
            return `Component at index ${i} has invalid type: ${component.type}`;
        }
        // Validate specific component types
        switch (component.type) {
            case "heading":
                if (typeof component.text !== "string") {
                    return `Heading at index ${i} is missing 'text' field`;
                }
                if (typeof component.level !== "number" ||
                    component.level < 1 ||
                    component.level > 6) {
                    return `Heading at index ${i} has invalid 'level' (must be 1-6)`;
                }
                break;
            case "text":
            case "markdown":
                if (typeof component.content !== "string") {
                    return `${component.type} at index ${i} is missing 'content' field`;
                }
                break;
            case "form":
                if (typeof component.action !== "string") {
                    return `Form at index ${i} is missing 'action' field`;
                }
                if (!Array.isArray(component.fields)) {
                    return `Form at index ${i} is missing 'fields' array`;
                }
                break;
            case "button":
                if (typeof component.label !== "string") {
                    return `Button at index ${i} is missing 'label' field`;
                }
                if (component.action !== "tx" && component.action !== "render") {
                    return `Button at index ${i} has invalid 'action' (must be 'tx' or 'render')`;
                }
                break;
            case "list":
                if (!Array.isArray(component.items)) {
                    return `List at index ${i} is missing 'items' array`;
                }
                break;
            case "task":
                if (typeof component.text !== "string") {
                    return `Task at index ${i} is missing 'text' field`;
                }
                if (typeof component.completed !== "boolean") {
                    return `Task at index ${i} is missing 'completed' field`;
                }
                break;
            case "navigation":
                if (!Array.isArray(component.items)) {
                    return `Navigation at index ${i} is missing 'items' array`;
                }
                break;
            case "container":
                if (!Array.isArray(component.components)) {
                    return `Container at index ${i} is missing 'components' array`;
                }
                // Recursively validate nested components
                const nestedError = validateComponents(component.components);
                if (nestedError) {
                    return nestedError;
                }
                break;
            case "include":
                if (typeof component.contract !== "string") {
                    return `Include at index ${i} is missing 'contract' field`;
                }
                break;
            case "chart":
                if (!["pie", "gauge", "bar"].includes(component.chartType)) {
                    return `Chart at index ${i} has invalid 'chartType' (must be 'pie', 'gauge', or 'bar')`;
                }
                if (component.chartType === "gauge") {
                    if (typeof component.value !== "number") {
                        return `Gauge chart at index ${i} is missing 'value' field`;
                    }
                    if (typeof component.max !== "number") {
                        return `Gauge chart at index ${i} is missing 'max' field`;
                    }
                }
                else {
                    // pie or bar
                    if (!Array.isArray(component.data)) {
                        return `${component.chartType} chart at index ${i} is missing 'data' array`;
                    }
                }
                break;
        }
    }
    return null;
}
/**
 * Check if content is JSON format
 */
export function isJsonFormat(content) {
    const trimmed = content.trim();
    if (!trimmed.startsWith("{")) {
        return false;
    }
    try {
        const parsed = JSON.parse(trimmed);
        return Boolean(parsed.format && parsed.format.startsWith("soroban-render-json"));
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=json.js.map